import React, { useEffect, useState } from "react";
import {
  CostingItem,
  Operation,
  PredefinedOp,
} from "../../interfaces/app_types";
import BulkOperations from "./BulkOperations";

interface StagingPanelProps {
  predefinedOps: PredefinedOp[];
  opsLoading: boolean;
  opsError: boolean;
  onApplyOperations: (indices: number[], operations: Operation[]) => void;
  stagedItems: CostingItem[];
  activeHistoryId: string | null;
  isSaving: boolean;
  onEdit: (index: number) => void;
  onClone: (item: CostingItem) => void;
  onRemove: (index: number) => void;
  onReorder: (from: number, to: number) => void;
  onCommit: () => void;
}

const StagingPanel: React.FC<StagingPanelProps> = ({
  predefinedOps,
  opsLoading,
  opsError,
  onApplyOperations,
  stagedItems,
  activeHistoryId,
  isSaving,
  onEdit,
  onClone,
  onRemove,
  onReorder,
  onCommit,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<CostingItem[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Object identity keeps selection on the same components when they are reordered.
  const selectedIndices = stagedItems.reduce<number[]>(
    (indices, item, index) =>
      selectedItems.includes(item) ? [...indices, index] : indices,
    []
  );
  const selectedCount = selectedIndices.length;
  useEffect(() => {
    if (!selectedCount) setBulkOpen(false);
  }, [selectedCount]);
  const clearDrag = () => {
    setDraggedIndex(null);
    setDropIndex(null);
  };

  return (
    <div className="costing-staging-panel">
      <div>
        <h3 className="costing-staging-title">
          Staged Components ({stagedItems.length})
          {activeHistoryId && (
            <span className="costing-staging-title__tag">
              editing saved run
            </span>
          )}
        </h3>
        {stagedItems.length > 1 && (
          <p className="costing-hint">
            Drag the handle or use the arrows to change order.
          </p>
        )}

        {stagedItems.length > 0 && (
          <div className="costing-bulk-toolbar">
            <label className="costing-component-select">
              <input
                type="checkbox"
                className="form-check-input"
                disabled={isSaving}
                checked={selectedCount === stagedItems.length}
                ref={(input) => {
                  if (input)
                    input.indeterminate =
                      selectedCount > 0 && selectedCount < stagedItems.length;
                }}
                onChange={(event) =>
                  setSelectedItems(event.target.checked ? [...stagedItems] : [])
                }
              />
              Select all
            </label>
            <span aria-live="polite">{selectedCount} selected</span>
            <button
              type="button"
              className="costing-sm-btn"
              disabled={!selectedCount || isSaving}
              onClick={() => setBulkOpen(true)}
            >
              Add operations
            </button>
            {selectedCount > 0 && (
              <button
                type="button"
                className="costing-reorder-btn"
                disabled={isSaving}
                onClick={() => setSelectedItems([])}
              >
                Clear
              </button>
            )}
          </div>
        )}
        {bulkOpen && stagedItems.length > 0 && (
          <BulkOperations
            predefinedOps={predefinedOps}
            opsLoading={opsLoading}
            opsError={opsError}
            selectedCount={selectedCount}
            isSaving={isSaving}
            onCancel={() => setBulkOpen(false)}
            onApply={(operations) => {
              onApplyOperations(selectedIndices, operations);
              setSelectedItems([]);
              setBulkOpen(false);
            }}
          />
        )}

        <div className="costing-staged-scroll">
          {stagedItems.length === 0 ? (
            <p className="costing-hint">No components staged yet.</p>
          ) : (
            stagedItems.map((item, idx) => (
              <div
                key={idx}
                className={`costing-staged-card${
                  selectedItems.includes(item)
                    ? " costing-staged-card--selected"
                    : ""
                }${
                  draggedIndex === idx ? " costing-staged-card--dragging" : ""
                }${
                  dropIndex === idx && draggedIndex !== idx
                    ? " costing-staged-card--drop-target"
                    : ""
                }`}
                onDragOver={(event) => {
                  if (draggedIndex === null || isSaving) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDropIndex(idx);
                }}
                onDragLeave={(event) => {
                  if (
                    !event.currentTarget.contains(
                      event.relatedTarget as Node | null
                    )
                  ) {
                    setDropIndex(null);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggedIndex !== null && !isSaving)
                    onReorder(draggedIndex, idx);
                  clearDrag();
                }}
              >
                <div className="costing-staged-card__header">
                  <label className="costing-component-select costing-staged-card__drawing">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      disabled={isSaving}
                      aria-label={`Select ${item.drawing_no}`}
                      checked={selectedItems.includes(item)}
                      onChange={(event) =>
                        setSelectedItems((prev) =>
                          event.target.checked
                            ? [
                                ...prev.filter((value) =>
                                  stagedItems.includes(value)
                                ),
                                item,
                              ]
                            : prev.filter((value) => value !== item)
                        )
                      }
                    />
                    {idx + 1}. {item.drawing_no}
                  </label>
                  <div className="costing-staged-card__order">
                    <button
                      type="button"
                      className="costing-reorder-btn costing-reorder-btn--drag"
                      draggable={stagedItems.length > 1 && !isSaving}
                      disabled={stagedItems.length < 2 || isSaving}
                      aria-label={`Drag to reorder ${item.drawing_no}`}
                      title="Drag to reorder; use arrow keys to move"
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", String(idx));
                        setDraggedIndex(idx);
                      }}
                      onDragEnd={clearDrag}
                      onKeyDown={(event) => {
                        if (
                          event.key !== "ArrowUp" &&
                          event.key !== "ArrowDown"
                        )
                          return;
                        event.preventDefault();
                        onReorder(
                          idx,
                          idx + (event.key === "ArrowUp" ? -1 : 1)
                        );
                      }}
                    >
                      <i className="bi bi-grip-vertical" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="costing-reorder-btn"
                      aria-label={`Move ${item.drawing_no} up`}
                      title="Move up"
                      disabled={idx === 0 || isSaving}
                      onClick={() => onReorder(idx, idx - 1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="costing-reorder-btn"
                      aria-label={`Move ${item.drawing_no} down`}
                      title="Move down"
                      disabled={idx === stagedItems.length - 1 || isSaving}
                      onClick={() => onReorder(idx, idx + 1)}
                    >
                      ↓
                    </button>
                  </div>
                </div>
                <p className="costing-staged-card__sheet">{item.part_name}</p>
                <p className="costing-staged-card__dims">
                  {item.length}×{item.breadth}×{item.thickness} mm | Qty:{" "}
                  {item.quantity}
                </p>
                {!!item.operations?.length && (
                  <p className="costing-staged-card__meta">
                    {item.operations.length} op(s)
                  </p>
                )}
                {!!item.tools?.length && (
                  <p className="costing-staged-card__meta">
                    {item.tools.length} tool(s)
                  </p>
                )}
                <div className="costing-staged-card__actions">
                  <button
                    className="costing-edit-btn"
                    onClick={() => onEdit(idx)}
                  >
                    Edit
                  </button>
                  <button
                    className="costing-clone-btn"
                    onClick={() => onClone(item)}
                  >
                    Clone
                  </button>
                  <button
                    className="costing-delete-btn"
                    onClick={() => onRemove(idx)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <button
        className={`costing-commit-btn${
          stagedItems.length === 0 ? " costing-commit-btn--disabled" : ""
        }`}
        onClick={onCommit}
        disabled={stagedItems.length === 0 || isSaving}
      >
        {isSaving
          ? "Saving…"
          : activeHistoryId
          ? "UPDATE COSTING"
          : "CREATE COSTING"}
      </button>
    </div>
  );
};

export default StagingPanel;
